import React, { useState } from "react";
import ImageListModal from "../../../CommonComponents/ImageListModal/ImageListModal";
import { Box, CircularProgress, Typography, useMediaQuery, useTheme } from "@mui/material";
import { ProgressReviewAPIHandler } from '../../../../services/NetworkClient/BackendAPIs/ProgressReviewAPIHandler';
import { useSignedResource } from '../../../../hooks/useSignedResource';

interface ProgressReviewCardProps {
    progressReviewId: string;
}

const fetchReview = (reviewId: string) => ProgressReviewAPIHandler.shared.getReviewById(reviewId);

const ProgressReviewCard = ({ progressReviewId }: ProgressReviewCardProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    
    const [openModal, setOpenModal] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);

    // Screenshot URLs are short-lived signed URLs, so they are fetched on render instead of
    // being cached in the chat message, and re-signed when the user comes back to the tab.
    const { data: progressReview, loading } = useSignedResource(progressReviewId, fetchReview);

    const getResponsiveStyles = () => ({
        reviewRequestMessage: { 
            width: '100%', 
            minWidth: isMobile ? '100%' : '500px', 
            minHeight: isMobile ? '300px' : '370px', 
            padding: isMobile ? '1rem' : '2rem', 
            borderRadius: isMobile ? '8px' : '10px', 
            backgroundColor: '#F8F5FE', 
            margin: isMobile ? '5px 0' : '10px 0' 
        },
        reviewRequestHeadline: { 
            fontWeight: 'bold', 
            fontSize: isMobile ? '1.2rem' : '1.5rem' 
        },
        reviewRequestContent: { 
            padding: isMobile ? '1rem' : '2rem', 
            borderRadius: isMobile ? '8px' : '10px', 
            backgroundColor: 'white', 
            margin: isMobile ? '0.75rem 0rem' : '1rem 0rem', 
            lineHeight: '1.7rem', 
            fontSize: isMobile ? '0.8rem' : '0.9rem' 
        },
        reviewRequestAttachments: { 
            display: 'flex' as const, 
            padding: isMobile ? '1rem' : '2rem', 
            borderRadius: isMobile ? '8px' : '10px', 
            backgroundColor: 'white', 
            margin: isMobile ? '0.75rem 0rem' : '1rem 0rem', 
            overflowX: 'auto' as const, 
            overflowY: 'hidden' as const, 
            whiteSpace: 'nowrap' as const, 
            maxWidth: '100%' 
        },
        revisionAttachmentBox: { 
            width: isMobile ? '4rem' : '5rem', 
            height: isMobile ? '4rem' : '5rem', 
            margin: isMobile ? '8px 8px' : '10px 10px', 
            borderRadius: isMobile ? '8px' : '10px', 
            backgroundColor: '#D7D7D7', 
            overflow: 'hidden' as const, 
            display: 'flex' as const, 
            alignItems: 'center' as const, 
            justifyContent: 'center' as const, 
            cursor: 'pointer' as const
        },
        revisionAttachmentImage: { 
            width: '100%', 
            height: "auto" 
        },
        remarksBlock: { 
            padding: isMobile ? '0.75rem' : '1rem', 
            borderRadius: isMobile ? '8px' : '10px', 
            backgroundColor: '#FFFFFF', 
            marginTop: isMobile ? '0.75rem' : '1rem' 
        },
    });

    const revisionStyle = getResponsiveStyles();

    const handleAttachmentClick = (idx: number) => {
        setCurrentIndex(idx);
        setOpenModal(true);
    };

    const handleCloseModal = () => setOpenModal(false);

    if (loading) {
        return (
            <div
                className="revisionRequestMessage"
                style={{
                    ...revisionStyle.reviewRequestMessage,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center"
                }}
            >
                <CircularProgress />
                <Box sx={{ width: "100%", height: 120, background: "#f3f3f3", borderRadius: "10px", marginTop: 3 }} />
            </div>
        );
    }

    if (!progressReview) {
        return (
            <div style={{ padding: "2rem" }}>
                <Typography color="error">Failed to load progress review.</Typography>
            </div>
        );
    }

    const screenshotUrls = progressReview.screenshotUrlList;

    return (
        <div className="revisionRequestMessage" style={revisionStyle.reviewRequestMessage}>
            <div className="revisionRequestHeadline" style={revisionStyle.reviewRequestHeadline}>Progress Review</div>
            {screenshotUrls.length > 0 &&
                <div className="revisionRequestAttachments" style={revisionStyle.reviewRequestAttachments}>{
                    screenshotUrls.map((item: string, idx: number) => (
                        <div
                            className='revisionAttachmentBox'
                            style={revisionStyle.revisionAttachmentBox}
                            key={idx}
                            onClick={() => handleAttachmentClick(idx)}
                        >
                            <img src={item} className='revisionAttachmentImage' style={revisionStyle.revisionAttachmentImage} alt='attachments' />
                        </div>
                    ))}
                </div>
            }
            <div className='remarks-block' style={revisionStyle.remarksBlock}>
                <Typography className='remarks-title' fontWeight={700} marginBottom={12}>Remarks</Typography>
                <div className="remarks-content">
                    {progressReview.remarks ?? "No remarks added for this progress review"}
                </div>
            </div>
            <ImageListModal
                open={openModal}
                images={screenshotUrls}
                initialIndex={currentIndex}
                onClose={handleCloseModal}
            />
        </div>
    );
};

export default ProgressReviewCard;